import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import FeaturedEventCard from '../components/events/FeaturedEventCard';
import UpcomingEventCard from '../components/events/UpcomingEventCard';
import EventTypeCard from '../components/events/EventTypeCard';
import { eventService } from '../services/eventService';
import { useAuth } from '../contexts/AuthContext';

const CATEGORY_ICON_MAP = {
  islamic: 'islamic', family: 'family', sports: 'sports', business: 'business',
  food: 'food', concerts: 'concerts', community: 'family', arts: 'concerts',
  tech: 'business', other: 'business',
};

const mapApiEvent = (e) => ({
  id: e.id,
  slug: e.slug,
  title: e.name || e.title,
  date: e.start_date
    ? new Date(e.start_date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      })
    : '',
  time: e.start_time ? e.start_time.slice(0, 5) : '',
  city: e.city || '',
  venue: e.venue || '',
  imageUrl: e.primary_image || e.images?.[0]?.original_image || '',
  category: e.category_display || e.category || '',
  priceLabel: e.is_free ? 'Free' : e.price ? `Rs. ${Number(e.price).toLocaleString()}` : 'Paid',
});

const buildDateStrip = (offset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + offset + i);
    days.push(d);
  }
  return days;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MAIN_PAGE_SIZE = 8;

const CalendarIcon = () => (
  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const EventsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);

  const [dateStripOffset, setDateStripOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);

  const featuredRef = useRef(null);
  const datePickerRef = useRef(null);
  const dateStripDays = useMemo(() => buildDateStrip(dateStripOffset), [dateStripOffset]);

  /* ── Data loaders ── */

  const loadFeatured = useCallback(async () => {
    try {
      const res = await eventService.getEvents({ page_size: 8, is_featured: true, time_frame: 'upcoming', ordering: '-start_date' });
      const data = res?.results ?? res?.data?.results ?? res?.data ?? res;
      setFeaturedEvents(Array.isArray(data) ? data.map(mapApiEvent) : []);
    } catch {
      setFeaturedEvents([]);
    }
  }, []);

  const loadUpcoming = useCallback(async (dateFilter) => {
    try {
      const params = { page_size: MAIN_PAGE_SIZE, page: 1, time_frame: 'upcoming', ordering: 'start_date' };
      if (dateFilter) params.start_date = dateFilter;
      const res = await eventService.getEvents(params);
      const data = res?.results ?? res?.data?.results ?? res?.data ?? res;
      const mapped = Array.isArray(data) ? data.map(mapApiEvent) : [];
      setUpcomingEvents(mapped.slice(0, MAIN_PAGE_SIZE));
    } catch {
      setUpcomingEvents([]);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const res = await eventService.getEvents({ page_size: MAIN_PAGE_SIZE, page: 1, time_frame: 'past', ordering: '-start_date' });
      const data = res?.results ?? res?.data?.results ?? res?.data ?? res;
      const mapped = Array.isArray(data) ? data.map(mapApiEvent) : [];
      setRecentEvents(mapped.slice(0, MAIN_PAGE_SIZE));
    } catch {
      setRecentEvents([]);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await eventService.getCategories();
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      setEventTypes(
        list
          .filter((c) => c.listing_count > 0)
          .map((c) => ({
            slug: c.slug,
            name: c.name,
            iconKey: CATEGORY_ICON_MAP[c.slug] || 'business',
            listingCount: c.listing_count,
          })),
      );
    } catch {
      setEventTypes([]);
    }
  }, []);

  /* ── Initial load ── */

  useEffect(() => {
    setLoading(true);
    setEventsError(null);
    Promise.all([loadFeatured(), loadUpcoming(null), loadRecent(), loadCategories()])
      .catch(() => setEventsError('Failed to load events. Please try again.'))
      .finally(() => setLoading(false));
  }, [loadFeatured, loadUpcoming, loadRecent, loadCategories]);

  /* ── Date strip ── */

  const handleDateSelect = useCallback((d) => {
    const iso = toISODate(d);
    if (selectedDate === iso) {
      setSelectedDate(null);
      loadUpcoming(null);
    } else {
      setSelectedDate(iso);
      loadUpcoming(iso);
    }
  }, [selectedDate, loadUpcoming]);

  const handleDatePickerChange = useCallback((e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedDate(null);
      loadUpcoming(null);
    } else {
      setSelectedDate(val);
      loadUpcoming(val);
    }
  }, [loadUpcoming]);

  const scrollFeatured = (dir) => {
    featuredRef.current?.scrollBy({ left: dir * 220, behavior: 'smooth' });
  };

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>Events in Pakistan - PakPoints</title>
        <meta name="description" content="Discover upcoming events near you across Pakistan." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="bg-[#f7f6f3] min-h-screen">

        {/* ═══ Header + Filters ═══ */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl text-gray-900">
                <span className="font-bold">Events</span>{' '}
                <span className="font-normal">in Pakistan</span>
              </h1>
              <nav className="text-xs text-gray-400" aria-label="Breadcrumb">
                <ol className="flex items-center gap-1.5">
                  <li className="flex items-center gap-1">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <Link to="/" className="hover:text-gray-600">Home</Link>
                  </li>
                  <li>/</li>
                  <li><Link to="/" className="hover:text-gray-600">Explore</Link></li>
                  <li>/</li>
                  <li className="text-gray-600 font-medium">Events</li>
                </ol>
              </nav>
            </div>
            <p className="text-gray-400 text-sm mb-6">Discover upcoming events near you.</p>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input type="text" placeholder="Search events..." className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400" />
              </div>
              <select className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-emerald-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_8px_center] pr-7">
                <option>Select City</option>
                <option>Lahore</option><option>Karachi</option><option>Islamabad</option><option>Rawalpindi</option>
              </select>
              <select className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-emerald-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_8px_center] pr-7">
                <option>All Categories</option>
                <option value="business">Business &amp; Seminars</option><option value="islamic">Islamic Events</option>
                <option value="family">Family &amp; Kids</option><option value="sports">Sports</option>
                <option value="food">Food Festivals</option><option value="concerts">Concerts</option>
              </select>
              <select className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-emerald-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_8px_center] pr-7">
                <option>Upcoming</option><option>Today</option><option>This Week</option>
                <option>This Month</option><option>Past Events</option>
              </select>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-800 transition-colors">
                Apply Filters
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
              </button>
            </div>
          </div>
        </div>

        {/* ═══ Main content ═══ */}
        <div className="max-w-7xl mx-auto px-4 py-8">

          {eventsError != null && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">{eventsError}</div>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
            </div>
          )}

          {!loading && (
            <>
              {/* ──── Featured Events ──── */}
              <section className="mb-10">
                <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                  <span className="text-lg text-amber-500">&#9733;</span>
                  Featured Events
                </h2>
                {featuredEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6">No featured events at the moment.</p>
                ) : (
                  <div className="relative">
                    <div ref={featuredRef} className="overflow-x-auto scrollbar-hide -mx-4 px-4 flex gap-4 pb-2">
                      {featuredEvents.map((event) => (
                        <FeaturedEventCard key={event.id} event={event} />
                      ))}
                    </div>
                    <button type="button" onClick={() => scrollFeatured(1)} className="absolute -right-1 top-[55px] w-7 h-7 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-gray-500 hover:bg-gray-50 z-10">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                    </button>
                  </div>
                )}
              </section>

              {/* ──── Upcoming Events ──── */}
              <section className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                    <CalendarIcon />
                    Upcoming Events
                  </h2>
                  <Link to="/events/upcoming" className="text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors flex items-center gap-1">
                    View All
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </Link>
                </div>

                {/* Date strip */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <input
                    ref={datePickerRef}
                    type="date"
                    className="sr-only"
                    value={selectedDate || ''}
                    onChange={handleDatePickerChange}
                    tabIndex={-1}
                    aria-label="Pick a date"
                  />

                  <button
                    type="button"
                    onClick={() => datePickerRef.current?.showPicker()}
                    className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                    </svg>
                    {selectedDate ? selectedDate : 'Pick a date'}
                    <span className="text-gray-400 hidden sm:inline">Show events by</span>
                  </button>

                  {selectedDate && (
                    <button
                      type="button"
                      onClick={() => { setSelectedDate(null); loadUpcoming(null); }}
                      className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      Clear
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}

                  <button type="button" onClick={() => setDateStripOffset((o) => o - 7)} className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                  </button>

                  <div className="flex gap-1 overflow-x-auto scrollbar-hide">
                    {dateStripDays.map((d) => {
                      const iso = toISODate(d);
                      const isActive = selectedDate === iso;
                      return (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => handleDateSelect(d)}
                          className={`flex-shrink-0 w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                            isActive
                              ? 'bg-gray-800 text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {d.getDate()}
                        </button>
                      );
                    })}
                  </div>

                  <button type="button" onClick={() => setDateStripOffset((o) => o + 7)} className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 flex items-center justify-center">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </button>
                </div>

                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6">
                    {selectedDate ? 'No events on this date.' : 'No upcoming events at the moment.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {upcomingEvents.map((event) => (
                      <UpcomingEventCard key={event.id} event={event} />
                    ))}
                  </div>
                )}

              </section>

              {/* ──── Recent Events (past) ──── */}
              <section className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                    <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recent Events
                  </h2>
                  <Link to="/events/recent" className="text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors flex items-center gap-1">
                    View All
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </Link>
                </div>

                {recentEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6">No recent events to show.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {recentEvents.map((event) => (
                      <UpcomingEventCard key={event.id} event={event} showAddToCalendar={false} />
                    ))}
                  </div>
                )}

              </section>

              {/* ──── Explore Events by Type ──── */}
              <section className="mb-10">
                <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-5">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  Explore Events by Type
                </h2>
                {eventTypes.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No event types available yet.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {eventTypes.map((type) => (
                      <EventTypeCard key={type.slug} type={type} />
                    ))}
                  </div>
                )}
              </section>

              {/* ──── Host an Event CTA ──── */}
              <section className="rounded-2xl bg-gradient-to-br from-emerald-50 via-emerald-50 to-green-100/80 border border-emerald-200/50 px-6 py-10 sm:px-10 text-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Hosting an Event?</h2>
                <p className="text-gray-600 mt-2 max-w-lg mx-auto text-sm">
                  Post your event on PakPoints and reach more people across Pakistan.
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-6">
                  <button type="button" onClick={() => navigate(isAuthenticated ? '/events/create' : '/?auth=login')} className="rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors shadow-sm">
                    Submit Event
                  </button>
                  <button type="button" className="rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                    Learn More
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default EventsPage;
